/**
 * High-level helpers used by both the CLI and the relayer to drive a deposit
 * end-to-end: parse note, look up pool, build the message, send.
 */
import { internal, toNano } from "@ton/ton";
import { Address } from "@ton/core";
import { generateNote, parseNote } from "./notes";
import { getPool } from "./deployments";
import { buildJettonDepositMessage, RECOMMENDED_JETTON_FORWARD_TON, } from "./jettons";
import { scanPoolTransactions } from "./indexer";
import { buildMerkleTree } from "./merkleTree";
import { generateMergeProof } from "./proof";
import { buildDepositBody } from "./pool";
import { sendTransferVersioned, } from "./walletLoader";
export async function depositNative(opts) {
    const pool = getPool(opts.network, "ton", opts.amount);
    if (!pool || !pool.address) {
        throw new Error(`No deployed pool for ton-${opts.amount} on ${opts.network}. ` +
            `Available denominations: see \`tonado pools list\`.`);
    }
    const { note, deposit } = await generateNote({
        assetId: "ton",
        amount: opts.amount,
        network: opts.network,
    });
    // Off-chain root + merge proof. The depositor replays LOG_DEPOSIT events to
    // rebuild the tree, generates a Groth16 proof that newRoot = insert(prevRoot,
    // commitment, leafIndex), and submits proof + roots to the pool. The pool
    // verifies the proof on-chain — accepting depositor-supplied roots without
    // an on-chain proof would let a depositor inject any root they like.
    const built = await buildDepositInputs(opts.client, pool.address, deposit.commitment);
    const denomination = BigInt(pool.denomination);
    const gas = opts.gasBudget ?? toNano("0.15");
    const body = await buildDepositBody({
        queryId: 0n,
        commitment: deposit.commitment,
        prevRoot: built.prevRoot,
        newRoot: built.newRoot,
        leafIndex: built.leafIndex,
        mergeProof: built.proof,
    });
    const seqno = await opts.wallet.getSeqno();
    // Version-agnostic send. V5R1 requires `authType: "external"` and a
    // concrete sendMode; V4 doesn't. `sendTransferVersioned` branches.
    const loaded = {
        wallet: opts.wallet,
        keyPair: opts.keyPair,
        address: opts.wallet.address.toString(),
        version: opts.walletVersion ?? "v5R1",
    };
    await sendTransferVersioned(loaded, {
        seqno,
        secretKey: opts.keyPair.secretKey,
        messages: [
            internal({
                to: Address.parse(pool.address),
                value: denomination + gas,
                body,
                bounce: true,
            }),
        ],
    });
    return { note, pool: pool.address, commitment: deposit.commitment, seqno };
}
/**
 * Replay LOG_DEPOSIT events from the pool, rebuild the merkle tree, compute
 * the path-for-next-insert, generate the merkle-update Groth16 proof, and
 * return everything needed to construct a valid DepositNative message body.
 *
 * Exported because the smoke-deposit script (and any other deposit caller
 * that bypasses `depositNative`) needs the same flow.
 *
 * Pass `clientOrLoader` as a TonClient (for server-side callers that
 * already have one) or as a CommitmentLoader function (for the browser
 * dApp, which gets commitments from the relayer instead of toncenter).
 */
export async function buildDepositInputs(clientOrLoader, poolAddress, newCommitment) {
    const existing = typeof clientOrLoader === "function"
        ? await clientOrLoader(poolAddress)
        : await loadAllCommitments(clientOrLoader, poolAddress);
    const tree = await buildMerkleTree({ leaves: existing });
    // Capture the path BEFORE inserting — that's the merkle path the circuit
    // wants. Then insert to get newRoot.
    const path = tree.pathForNextInsert();
    const prevRoot = tree.root;
    const leafIndex = tree.leafCount;
    const newRoot = await tree.insertLeaf(newCommitment);
    const { proof } = await generateMergeProof({
        prevRoot,
        newRoot,
        commitment: newCommitment,
        leafIndex,
        pathElements: path.pathElements,
        pathIndices: path.pathIndices,
    });
    return { prevRoot, newRoot, leafIndex, proof };
}
/**
 * Replay LOG_DEPOSIT events from the pool back through history and return the
 * commitments in deposit order (oldest first).
 *
 * Termination: stop only when the indexer reports it has exhausted the
 * transaction history — i.e., `lastLt` comes back undefined (no transactions
 * on that page) or equal to the cursor we already passed in (no progress).
 *
 * Importantly, do NOT stop merely because a particular transaction page
 * carried zero deposit events. A page can legitimately contain only
 * withdraws, admin ops, top-ups, or failed messages while older pages still
 * contain deposits — stopping there would yield an incomplete tree and the
 * pool would reject the next deposit with `ERR_PROOF_INVALID`.
 */
export async function loadAllCommitments(client, poolAddress) {
    const collected = [];
    let cursor = {};
    while (true) {
        const { events, lastLt, lastHash } = await scanPoolTransactions(client, {
            pool: Address.parse(poolAddress),
            maxTransactions: 256,
            fromLt: cursor.lt,
            fromHash: cursor.hash,
        });
        for (const e of events) {
            collected.push({ commitment: e.commitment, leafIndex: e.leafIndex });
        }
        if (!lastLt || (cursor.lt !== undefined && lastLt === cursor.lt))
            break;
        cursor = { lt: lastLt, hash: lastHash };
    }
    // Pages are scanned newest-first and txs within a page are newest-first,
    // so `collected` is roughly newest-first overall. The merkle tree needs
    // leaves in CHRONOLOGICAL (leafIndex 0 first) order — sort explicitly by
    // the on-chain leafIndex so we don't depend on scan-order assumptions.
    collected.sort((a, b) => a.leafIndex - b.leafIndex);
    return collected.map((e) => e.commitment);
}
export async function depositJetton(opts) {
    const pool = getPool(opts.network, opts.assetId, opts.amount);
    if (!pool || !pool.address) {
        throw new Error(`No deployed pool for ${opts.assetId}-${opts.amount} on ${opts.network}.`);
    }
    const { note, deposit } = await generateNote({
        assetId: opts.assetId,
        amount: opts.amount,
        network: opts.network,
    });
    // Same off-chain root + merge-proof pipeline as the native deposit:
    // replay LOG_DEPOSIT events, rebuild the tree, generate the
    // merkleUpdate Groth16 proof. The pool's transfer_notification
    // handler verifies the proof on-chain — see pool_jetton.tolk.
    const built = await buildDepositInputs(opts.client, pool.address, deposit.commitment);
    // High-level helper assembles the TEP-74 transfer + forward_payload
    // and returns the destination (the depositor's own jetton wallet)
    // along with the body the wallet should sign.
    const { destination, body } = await buildJettonDepositMessage({
        client: opts.client,
        jettonMaster: opts.jettonMaster,
        depositor: opts.wallet.address,
        poolAddress: Address.parse(pool.address),
        jettonAmount: BigInt(pool.denomination),
        commitment: deposit.commitment,
        prevRoot: built.prevRoot,
        newRoot: built.newRoot,
        leafIndex: built.leafIndex,
        mergeProof: built.proof,
        forwardTonAmount: opts.forwardTon ?? RECOMMENDED_JETTON_FORWARD_TON,
    });
    const seqno = await opts.wallet.getSeqno();
    const loaded = {
        wallet: opts.wallet,
        keyPair: opts.keyPair,
        address: opts.wallet.address.toString(),
        version: opts.walletVersion ?? "v5R1",
    };
    // The depositor pays `forwardTonAmount + small fee` to their OWN
    // jetton wallet. That wallet decrements the depositor's jetton
    // balance, sends an internal_transfer to the pool's jetton wallet
    // with the forward_payload + forward_ton_amount intact, and the pool
    // wallet then emits the transfer_notification to the pool.
    await sendTransferVersioned(loaded, {
        seqno,
        secretKey: opts.keyPair.secretKey,
        messages: [
            internal({
                to: destination,
                value: (opts.forwardTon ?? RECOMMENDED_JETTON_FORWARD_TON) + toNano("0.1"),
                body,
                bounce: true,
            }),
        ],
    });
    return { note, pool: pool.address, commitment: deposit.commitment, seqno };
}
/**
 * Reconcile a freshly produced note against a parsed copy (defensive sanity check).
 */
export async function reconcileNote(note) {
    return parseNote(note);
}
//# sourceMappingURL=depositFlow.js.map